/**
 * ClaimSync Sign In Page
 * Split-screen design with branding panel and login form
 */

import React from 'react';
import { SignIn, useUser, useClerk } from '@clerk/clerk-react';
import { Link, Navigate } from 'react-router-dom';
import { Calculator, FileText, BarChart3, Shield, Scale } from 'lucide-react';

export default function SignInPage() {
  const { isSignedIn, isLoaded } = useUser();

  // Redirect if already signed in
  if (isLoaded && isSignedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-gradient-to-br from-[#0b2545] via-[#134e6f] to-[#0b2545] p-12 flex-col justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img 
            src="/logo.svg" 
            alt="ClaimSync" 
            className="h-44 lg:h-56 w-auto"
          />
        </div>

        {/* Main Content */}
        <div className="max-w-xl">
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            AI-Powered Business Interruption Claims
          </h1>
          <p className="text-lg text-blue-200 mb-12 leading-relaxed">
            Transform your claims process with intelligent BI calculations, automated analysis, 
            and comprehensive financial reporting for business interruption cases.
          </p>

          {/* Feature Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-5 h-5 text-blue-300" />
                <span className="text-3xl font-bold text-white">BI</span>
              </div>
              <p className="text-blue-200 text-sm">Calculation Engine</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-blue-300" />
                <span className="text-3xl font-bold text-white">100+</span>
              </div>
              <p className="text-blue-200 text-sm">Industry Templates</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-blue-300" />
                <span className="text-3xl font-bold text-white">7-Step</span>
              </div>
              <p className="text-blue-200 text-sm">Wizard Process</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-5 h-5 text-blue-300" />
                <span className="text-3xl font-bold text-white">Monte Carlo</span>
              </div>
              <p className="text-blue-200 text-sm">Risk Analysis</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-blue-300 text-sm">
          © {new Date().getFullYear()} ClaimSync. Professional business interruption claim calculations.
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-[45%] flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-32 w-auto mx-auto mb-4"
            />
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
            <p className="text-gray-600">Sign in to your account to continue</p>
          </div>

          {/* Clerk Sign In */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-1">
            <SignIn 
              routing="hash"
              signUpUrl="/sign-up"
              forceRedirectUrl="/"
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "shadow-none border-0 bg-transparent",
                  headerTitle: "hidden",
                  headerSubtitle: "hidden",
                  socialButtonsBlockButton: "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors",
                  socialButtonsBlockButtonText: "font-medium",
                  dividerLine: "bg-gray-200",
                  dividerText: "text-gray-500 text-sm",
                  formFieldLabel: "text-gray-700 font-medium",
                  formFieldInput: "bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg",
                  formButtonPrimary: "bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-3 font-semibold transition-colors",
                  footerActionLink: "text-blue-600 hover:text-blue-700 font-medium",
                  identityPreviewText: "text-gray-700",
                  identityPreviewEditButton: "text-blue-600",
                  formFieldSuccessText: "text-green-600",
                  formFieldErrorText: "text-red-600",
                  alertText: "text-gray-700",
                  otpCodeFieldInput: "border-gray-200 focus:border-blue-500",
                  formResendCodeLink: "text-blue-600 hover:text-blue-700"
                },
                layout: {
                  socialButtonsPlacement: "bottom",
                  showOptionalFields: false
                }
              }}
            />
          </div>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/sign-up" className="text-blue-600 hover:text-blue-700 font-semibold">
                Sign up
              </Link>
            </p>
          </div>

          {/* Security Badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-gray-400 text-sm">
            <Shield className="w-4 h-4" />
            <span>Secured by Clerk</span>
          </div>
        </div>
      </div>
    </div>
  );
}
